import type { Editor } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"
import { OPERATION_CONTRACTS } from "../analytics/operations"
import { getCardId } from "./card"

/**
 * Backspace with the cursor in an EMPTY card title dissolves the card and merges
 * its content into the card above.
 *
 * Returns false (unhandled) unless the cursor is an empty TextSelection in an
 * empty cardTitle with a card sibling above — the caller falls back to
 * cancelBackspaceAtNodeStart for every other case.
 *
 * The merge is a single delete step spanning [end of the previous card's content,
 * end of the empty title]: it removes the previous card's closing token, this
 * card's opening token, and the title node, so ProseMirror joins the two cards.
 * A node-level tr.join can't express this — the merged card would carry a second
 * cardTitle mid-content, which the card schema rejects.
 */
export function mergeEmptyCardIntoCardAbove(editor: Editor): boolean {
  const { selection, doc } = editor.state
  if (!(selection instanceof TextSelection && selection.empty)) return false

  const { $from } = selection
  if ($from.parent.type.name !== "cardTitle") return false
  if ($from.parent.content.size !== 0) return false
  if ($from.depth !== 2) return false

  const cardIndex = $from.index(0)
  if (cardIndex === 0) return false
  // Only a titledCard or untitledCard can receive the merged content (a cardGoogleClassroom is atomic).
  if (!["titledCard", "untitledCard"].includes(doc.child(cardIndex - 1).type.name)) {
    return false
  }

  // The card's first content block (the schema guarantees one after the title).
  const firstMerged = $from.node(1).maybeChild(1)
  const from = $from.before(1) - 1
  const to = $from.after(2)

  const sourceCardId = getCardId($from.node(1))
  const destinationCardId = getCardId(doc.child(cardIndex - 1))
  const chain = editor.chain()
  if (sourceCardId && destinationCardId) {
    chain.label(OPERATION_CONTRACTS.OP_MERGE_CARD_BACKWARD, "keyboard", {
      sourceCardId,
      destinationCardId,
    })
  }
  return chain
    .command(({ tr }) => {
      tr.delete(from, to)
      // Cursor at the join: the start of the merged content, or — when that block
      // is a widget with no text position — the nearest position before it.
      tr.setSelection(TextSelection.near(tr.doc.resolve(from), firstMerged?.isTextblock ? 1 : -1))
      tr.scrollIntoView()
      return true
    })
    .run()
}
